import React, { useMemo, useState } from "react";
import "./CriteriaExplorer.css";

/** ---------- 1) FLATTEN EACH DOC INTO A SINGLE ROW ---------- */
function num(n) {
  const v = Number(n);
  return Number.isFinite(v) ? v : null;
}
function bool(b) {
  return typeof b === "boolean" ? b : null;
}
function deriveSide(doc) {
  const rec = (doc?.analysis?.recommendation || "").toUpperCase();
  if (["BUY", "STRONG BUY", "LONG", "STRONG LONG"].some(s => rec.includes(s))) return "LONG";
  if (["SELL", "STRONG SELL", "SHORT", "STRONG SHORT"].some(s => rec.includes(s))) return "SHORT";
  return "NA";
}

function flattenDoc(d) {
  const tf = d?.timeframesUsed || {};
  const daily  = tf.daily     || {};
  const hourly = tf.hourly    || {};
  const short  = tf.shortTerm || {};

  const mc  = d?.marketContext || {};
  const ind = d?.indicators || {};

  return {
    id: d._id,
    token: d.token,
    timestamp: d.timestamp,

    // outcome
    targetHit: d?.outcome?.targetHit || "none",
    profitLoss: num(d?.outcome?.profitLoss),
    daysToOutcome: num(d?.outcome?.daysToOutcome),
    aiAccuracy: d?.outcome?.aiAccuracyRating || null,

    // analysis basics
    score: num(d?.analysis?.score),
    scoreMeaning: d?.analysis?.scoreMeaning || null,
    confidence: num(d?.analysis?.confidenceLevel),
    priceChange24h: num(d?.analysis?.priceChange24h),
    source: d?.analysis?.source || null,
    side: deriveSide(d),
    trendStrength: d?.analysis?.trendStrength || null,

    // indicators (top-level)
    ind_macd: num(ind.macd),
    ind_macdSignal: num(ind.macdSignal),
    ind_rsi: num(ind.rsi),
    ind_vol: num(ind.vol),
    ind_volMA: num(ind.volMA),

    // market context
    aiLabel: mc.aiLabel || null,
    aiConfidence: num(mc.aiConfidence),
    expDir: mc.expectedDirection24h || null,
    trendPhase: mc.trendPhase || null,
    total3Trend: mc.total3Trend || null,
    total3TrendShort: mc.total3TrendShort || null,
    usdtDomTrend: mc.usdtDominanceTrend || null,
    usdtDomTrendShort: mc.usdtDominanceTrendShort || null,
    ethBTCTrend: mc.ethBTCTrend || null,
    ethBTCTrendShort: mc.ethBTCTrendShort || null,
    volumeTopCryptosTrend: mc.volumeTopCryptosTrend || null,
    volumeTopCryptosTrendShort: mc.volumeTopCryptosTrendShort || null,

    // timeframe: DAILY
    daily_isPotentialTop: bool(daily.isPotentialTop),
    daily_maBias: daily.maBias || null,
    daily_macdCross: daily.macdCross || null,
    daily_rsiTrend: daily.rsiTrend || null,
    daily_volumeTrend: daily.volumeTrend || null,
    daily_macd: num(daily.macd),
    daily_macdSignal: num(daily.macdSignal),
    daily_macd20: num(daily.macd20),
    daily_macd50: num(daily.macd50),
    daily_rsi: num(daily.rsi),
    daily_volume: num(daily.volume),

    // timeframe: HOURLY
    hourly_isPotentialTop: bool(hourly.isPotentialTop),
    hourly_maBias: hourly.maBias || null,
    hourly_macdCross: hourly.macdCross || null,
    hourly_rsiTrend: hourly.rsiTrend || null,
    hourly_volumeTrend: hourly.volumeTrend || null,
    hourly_macd: num(hourly.macd),
    hourly_macdSignal: num(hourly.macdSignal),
    hourly_macd20: num(hourly.macd20),
    hourly_macd50: num(hourly.macd50),
    hourly_rsi: num(hourly.rsi),
    hourly_volume: num(hourly.volume),

    // timeframe: SHORT TERM
    short_isPotentialTop: bool(short.isPotentialTop),
    short_maBias: short.maBias || null,
    short_macdCross: short.macdCross || null,
    short_rsiTrend: short.rsiTrend || null,
    short_volumeTrend: short.volumeTrend || null,
    short_macd: num(short.macd),
    short_macdSignal: num(short.macdSignal),
    short_macd20: num(short.macd20),
    short_macd50: num(short.macd50),
    short_rsi: num(short.rsi),
    short_volume: num(short.volume),
  };
}

/** ---------- 2) FIELD CONFIGS (what to expose in the UI & table) ---------- */
const CAT_FIELDS = [
  // outcome & basics
  "side", "targetHit", "aiAccuracy", "source", "trendStrength",

  // market context
  "aiLabel", "expDir", "trendPhase",
  "total3Trend", "total3TrendShort",
  "usdtDomTrend", "usdtDomTrendShort",
  "ethBTCTrend", "ethBTCTrendShort",
  "volumeTopCryptosTrend", "volumeTopCryptosTrendShort",

  // timeframe categorical
  "daily_maBias", "daily_macdCross", "daily_rsiTrend", "daily_volumeTrend",
  "hourly_maBias", "hourly_macdCross", "hourly_rsiTrend", "hourly_volumeTrend",
  "short_maBias", "short_macdCross", "short_rsiTrend", "short_volumeTrend",
];

const BOOL_FIELDS = [
  "daily_isPotentialTop",
  "hourly_isPotentialTop",
  "short_isPotentialTop",
];

const NUM_FIELDS = [
  // outcome & analysis numbers
  "profitLoss", "daysToOutcome", "score", "confidence", "priceChange24h",

  // indicators
  "ind_macd", "ind_macdSignal", "ind_rsi", "ind_vol", "ind_volMA",

  // market context numeric
  "aiConfidence",

  // timeframe numeric indicators
  "daily_macd", "daily_macdSignal", "daily_macd20", "daily_macd50", "daily_rsi", "daily_volume",
  "hourly_macd", "hourly_macdSignal", "hourly_macd20", "hourly_macd50", "hourly_rsi", "hourly_volume",
  "short_macd", "short_macdSignal", "short_macd20", "short_macd50", "short_rsi", "short_volume",
];

const ALL_FIELDS = [...CAT_FIELDS, ...BOOL_FIELDS, ...NUM_FIELDS];

/** friendly labels for the table/selector */
const LABELS = Object.fromEntries(
  [
    ["timestamp","Time"],["token","Token"],
    ["targetHit","Hit"],["profitLoss","P/L %"],["daysToOutcome","Days to Outcome"],["aiAccuracy","AI Accuracy"],
    ["score","Score"],["scoreMeaning","Score Meaning"],["confidence","Confidence"],["priceChange24h","24h %"],
    ["source","Source"],["side","Side"],["trendStrength","Trend"],
    ["ind_macd","ind.macd"],["ind_macdSignal","ind.macdSignal"],["ind_rsi","ind.rsi"],["ind_vol","ind.vol"],["ind_volMA","ind.volMA"],
    ["aiLabel","aiLabel"],["aiConfidence","aiConfidence"],["expDir","exp.Dir"],["trendPhase","trendPhase"],
    ["total3Trend","total3Trend"],["total3TrendShort","total3TrendShort"],
    ["usdtDomTrend","usdtDomTrend"],["usdtDomTrendShort","usdtDomTrendShort"],
    ["ethBTCTrend","ethBTCTrend"],["ethBTCTrendShort","ethBTCTrendShort"],
    ["volumeTopCryptosTrend","volTopTrend"],["volumeTopCryptosTrendShort","volTopTrendShort"],
    ["daily_isPotentialTop","daily.isTop"],["daily_maBias","daily.maBias"],["daily_macdCross","daily.macdCross"],
    ["daily_rsiTrend","daily.rsiTrend"],["daily_volumeTrend","daily.volTrend"],
    ["daily_macd","daily.macd"],["daily_macdSignal","daily.macdSignal"],["daily_macd20","daily.macd20"],
    ["daily_macd50","daily.macd50"],["daily_rsi","daily.rsi"],["daily_volume","daily.volume"],
    ["hourly_isPotentialTop","hourly.isTop"],["hourly_maBias","hourly.maBias"],["hourly_macdCross","hourly.macdCross"],
    ["hourly_rsiTrend","hourly.rsiTrend"],["hourly_volumeTrend","hourly.volTrend"],
    ["hourly_macd","hourly.macd"],["hourly_macdSignal","hourly.macdSignal"],["hourly_macd20","hourly.macd20"],
    ["hourly_macd50","hourly.macd50"],["hourly_rsi","hourly.rsi"],["hourly_volume","hourly.volume"],
    ["short_isPotentialTop","short.isTop"],["short_maBias","short.maBias"],["short_macdCross","short.macdCross"],
    ["short_rsiTrend","short.rsiTrend"],["short_volumeTrend","short.volTrend"],
    ["short_macd","short.macd"],["short_macdSignal","short.macdSignal"],["short_macd20","short.macd20"],
    ["short_macd50","short.macd50"],["short_rsi","short.rsi"],["short_volume","short.volume"],
  ]
);

/** collect distinct options for categorical filters */
function optionsFor(rows, key) {
  const s = new Set();
  rows.forEach(r => { if (r[key] !== null && r[key] !== undefined) s.add(r[key]); });
  return Array.from(s).sort((a,b) => String(a).localeCompare(String(b)));
}

/** ---------- 3) MAIN COMPONENT ---------- */
export default function CriteriaExplorer({ docs = [], onApply }) {
  const rows = useMemo(() => docs.map(flattenDoc), [docs]);

  // options per categorical field
  const catOptions = useMemo(() => {
    const m = {};
    CAT_FIELDS.forEach((k) => (m[k] = optionsFor(rows, k)));
    return m;
  }, [rows]);

  // filters state
  const [catFilters, setCatFilters] = useState(() =>
    Object.fromEntries(CAT_FIELDS.map(k => [k, new Set()]))
  );
  const [boolFilters, setBoolFilters] = useState(() =>
    Object.fromEntries(BOOL_FIELDS.map(k => [k, "any"])) // "any" | "yes" | "no"
  );
  const [numFilters, setNumFilters] = useState(() =>
    Object.fromEntries(NUM_FIELDS.map(k => [k, { min: "", max: "" }]))
  );

  // table sorting + visible columns
  const [sortKey, setSortKey] = useState("profitLoss");
  const [sortDir, setSortDir] = useState("desc");

  // start with ALL filters shown in the table (plus fixed token/time)
  const [visibleCols, setVisibleCols] = useState(() => new Set(ALL_FIELDS));
  const [colSearch, setColSearch] = useState("");

  const predicate = (r) => {
    // categorical
    for (const key of CAT_FIELDS) {
      const selected = catFilters[key];
      if (selected && selected.size) {
        if (!selected.has(r[key])) return false;
      }
    }
    // booleans
    for (const key of BOOL_FIELDS) {
      const mode = boolFilters[key];
      if (mode === "yes" && r[key] !== true) return false;
      if (mode === "no"  && r[key] !== false) return false;
    }
    // numeric
    for (const key of NUM_FIELDS) {
      const { min, max } = numFilters[key] || {};
      const v = r[key];
      if (min !== "" && v !== null && v !== undefined && !(v >= Number(min))) return false;
      if (max !== "" && v !== null && v !== undefined && !(v <= Number(max))) return false;
    }
    return true;
  };

  const filtered = useMemo(() => rows.filter(predicate), [rows, catFilters, boolFilters, numFilters]);

  const sorted = useMemo(() => {
    const arr = [...filtered];
    arr.sort((a, b) => {
      const av = a[sortKey];
      const bv = b[sortKey];
      if (typeof av === "string" && typeof bv === "string") {
        return sortDir === "asc" ? av.localeCompare(bv) : bv.localeCompare(av);
      }
      if (av == null) return 1;
      if (bv == null) return -1;
      return sortDir === "asc" ? (av - bv) : (bv - av);
    });
    return arr;
  }, [filtered, sortKey, sortDir]);

  const resetAll = () => {
    setCatFilters(Object.fromEntries(CAT_FIELDS.map(k => [k, new Set()])));
    setBoolFilters(Object.fromEntries(BOOL_FIELDS.map(k => [k, "any"])));
    setNumFilters(Object.fromEntries(NUM_FIELDS.map(k => [k, { min: "", max: "" }])));
  };

  const toggleCol = (key) => {
    const next = new Set(visibleCols);
    if (next.has(key)) next.delete(key); else next.add(key);
    setVisibleCols(next);
  };
  const showAllCols = () => setVisibleCols(new Set(ALL_FIELDS));
  const hideAllCols = () => setVisibleCols(new Set());

  const filteredColumnList = ALL_FIELDS.filter(k =>
    (LABELS[k] || k).toLowerCase().includes(colSearch.toLowerCase())
  );

  const displayedCols = ALL_FIELDS.filter(k => visibleCols.has(k));

  return (
    <div className="cx">
      {/* LEFT: CONTROLS */}
      <div className="cx-controls">
        <h3 className="cx-title">Advanced Criteria</h3>

        <details className="cx-section" open>
          <summary className="cx-summary">Market Context</summary>
          {[
            "aiLabel","expDir","trendPhase",
            "total3Trend","total3TrendShort",
            "usdtDomTrend","usdtDomTrendShort",
            "ethBTCTrend","ethBTCTrendShort",
            "volumeTopCryptosTrend","volumeTopCryptosTrendShort"
          ].map((key) => (
            <MultiSelect
              key={key}
              label={LABELS[key] || key}
              options={catOptions[key]}
              value={catFilters[key]}
              onChange={(set) => setCatFilters(prev => ({ ...prev, [key]: set }))}
            />
          ))}
          <Range label={LABELS.aiConfidence} v={numFilters.aiConfidence}
                 onChange={(v)=>setNumFilters(p=>({...p, aiConfidence:v}))}/>
        </details>

        <details className="cx-section">
          <summary className="cx-summary">Timeframes — Daily</summary>
          {["daily_maBias","daily_macdCross","daily_rsiTrend","daily_volumeTrend"].map((key) => (
            <MultiSelect
              key={key}
              label={LABELS[key] || key}
              options={catOptions[key]}
              value={catFilters[key]}
              onChange={(set) => setCatFilters(prev => ({ ...prev, [key]: set }))}
            />
          ))}
          <TriState label={LABELS["daily_isPotentialTop"]}
                    value={boolFilters["daily_isPotentialTop"]}
                    onChange={(v) => setBoolFilters(p => ({ ...p, daily_isPotentialTop: v }))}/>
          {["daily_macd","daily_macdSignal","daily_macd20","daily_macd50","daily_rsi","daily_volume"].map((k) => (
            <Range key={k} label={LABELS[k] || k} v={numFilters[k]} onChange={(v)=>setNumFilters(p=>({...p,[k]:v}))}/>
          ))}
        </details>

        <details className="cx-section">
          <summary className="cx-summary">Timeframes — Hourly</summary>
          {["hourly_maBias","hourly_macdCross","hourly_rsiTrend","hourly_volumeTrend"].map((key) => (
            <MultiSelect
              key={key}
              label={LABELS[key] || key}
              options={catOptions[key]}
              value={catFilters[key]}
              onChange={(set) => setCatFilters(prev => ({ ...prev, [key]: set }))}
            />
          ))}
          <TriState label={LABELS["hourly_isPotentialTop"]}
                    value={boolFilters["hourly_isPotentialTop"]}
                    onChange={(v) => setBoolFilters(p => ({ ...p, hourly_isPotentialTop: v }))}/>
          {["hourly_macd","hourly_macdSignal","hourly_macd20","hourly_macd50","hourly_rsi","hourly_volume"].map((k) => (
            <Range key={k} label={LABELS[k] || k} v={numFilters[k]} onChange={(v)=>setNumFilters(p=>({...p,[k]:v}))}/>
          ))}
        </details>

        <details className="cx-section">
          <summary className="cx-summary">Timeframes — Short Term</summary>
          {["short_maBias","short_macdCross","short_rsiTrend","short_volumeTrend"].map((key) => (
            <MultiSelect
              key={key}
              label={LABELS[key] || key}
              options={catOptions[key]}
              value={catFilters[key]}
              onChange={(set) => setCatFilters(prev => ({ ...prev, [key]: set }))}
            />
          ))}
          <TriState label={LABELS["short_isPotentialTop"]}
                    value={boolFilters["short_isPotentialTop"]}
                    onChange={(v) => setBoolFilters(p => ({ ...p, short_isPotentialTop: v }))}/>
          {["short_macd","short_macdSignal","short_macd20","short_macd50","short_rsi","short_volume"].map((k) => (
            <Range key={k} label={LABELS[k] || k} v={numFilters[k]} onChange={(v)=>setNumFilters(p=>({...p,[k]:v}))}/>
          ))}
        </details>

        <details className="cx-section">
          <summary className="cx-summary">Outcome & Indicators</summary>
          {["side","targetHit","aiAccuracy","source","trendStrength"].map((key) => (
            <MultiSelect
              key={key}
              label={LABELS[key] || key}
              options={catOptions[key]}
              value={catFilters[key]}
              onChange={(set) => setCatFilters(prev => ({ ...prev, [key]: set }))}
            />
          ))}
          {["profitLoss","daysToOutcome","score","confidence","priceChange24h",
            "ind_macd","ind_macdSignal","ind_rsi","ind_vol","ind_volMA",
          ].map((k)=>(
            <Range key={k} label={LABELS[k] || k} v={numFilters[k]} onChange={(v)=>setNumFilters(p=>({...p,[k]:v}))}/>
          ))}
        </details>

        <div className="cx-actions">
          <button className="btn" onClick={resetAll}>Reset</button>
          {onApply && (
            <button className="btn btn-primary" onClick={() => onApply(() => predicate)}>
              Apply to dashboard
            </button>
          )}
        </div>

        <div className="cx-sorting">
          <label>Sort by</label>
          <select value={sortKey} onChange={(e)=>setSortKey(e.target.value)}>
            {[...ALL_FIELDS].map(k => <option key={k} value={k}>{LABELS[k] || k}</option>)}
          </select>
          <select value={sortDir} onChange={(e)=>setSortDir(e.target.value)}>
            <option value="desc">desc</option>
            <option value="asc">asc</option>
          </select>
        </div>

        <details className="cx-section">
          <summary className="cx-summary">Columns</summary>
          <div className="cx-colbar">
            <input
              className="cx-input"
              placeholder="Search columns…"
              value={colSearch}
              onChange={(e)=>setColSearch(e.target.value)}
            />
            <div className="cx-colbtns">
              <button className="btn" onClick={showAllCols} type="button">Show all</button>
              <button className="btn" onClick={hideAllCols} type="button">Hide all</button>
            </div>
          </div>
          <div className="cx-cols">
            {filteredColumnList.map(k => (
              <label key={k} className="cx-colopt">
                <input
                  type="checkbox"
                  checked={visibleCols.has(k)}
                  onChange={()=>toggleCol(k)}
                />
                <span>{LABELS[k] || k}</span>
              </label>
            ))}
          </div>
        </details>
      </div>

      {/* RIGHT: RESULTS (fixed height, scrolls) */}
      <div className="cx-results">
        <div className="cx-results-head">
          <div><strong>{sorted.length}</strong> matches</div>
          <div className="cx-results-hint">Scroll → to see more columns</div>
        </div>

        <div className="cx-table-wrap">
          <table className="cx-table">
            <thead>
              <tr>
                <th>{LABELS.timestamp}</th>
                <th>{LABELS.token}</th>
                {displayedCols.map((k) => (
                  <th key={k}>{LABELS[k] || k}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {sorted.map((r) => (
                <tr key={r.id}>
                  <td>{r.timestamp ? new Date(r.timestamp).toLocaleString() : "-"}</td>
                  <td>{r.token}</td>
                  {displayedCols.map((k) => (
                    <td key={k}>{formatCell(r[k])}</td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** ---------- SMALL INPUT SUBCOMPONENTS ---------- */
function MultiSelect({ label, options, value, onChange }) {
  const toggle = (opt) => {
    const next = new Set(value);
    if (next.has(opt)) next.delete(opt); else next.add(opt);
    onChange(next);
  };
  return (
    <div className="cx-field">
      <div className="cx-label">{label}</div>
      <div className="cx-chiprow">
        {(options.length ? options : ["—"]).map(opt => (
          <button
            key={String(opt)}
            onClick={(e)=>{ e.preventDefault(); if (opt !== "—") toggle(opt); }}
            className={`pill ${value.has(opt) ? "active" : ""} ${opt === "—" ? "disabled" : ""}`}
            disabled={opt === "—"}
          >
            {String(opt)}
          </button>
        ))}
      </div>
    </div>
  );
}

function TriState({ label, value, onChange }) {
  return (
    <div className="cx-field">
      <div className="cx-label">{label}</div>
      <div className="cx-chiprow">
        {["any","yes","no"].map(v => (
          <button
            key={v}
            className={`pill ${value===v?"active":""}`}
            onClick={(e)=>{ e.preventDefault(); onChange(v); }}
          >
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}

function Range({ label, v, onChange }) {
  return (
    <div className="cx-field">
      <div className="cx-label">{label}</div>
      <div className="cx-range">
        <input
          className="cx-input"
          type="number" step="any" placeholder="min"
          value={v.min} onChange={(e)=>onChange({ ...v, min: e.target.value })}
        />
        <input
          className="cx-input"
          type="number" step="any" placeholder="max"
          value={v.max} onChange={(e)=>onChange({ ...v, max: e.target.value })}
        />
      </div>
    </div>
  );
}

/** ---------- helpers ---------- */
function formatCell(v) {
  if (v === null || v === undefined) return "—";
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "number") {
    // Show 2 decimals for most numeric fields
    return Number.isFinite(v) ? v.toFixed(2) : String(v);
  }
  return String(v);
}
